import React, { createContext, useContext, useState, useEffect } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material'; // Thêm useTheme
import { keyframes } from '@mui/system';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { initializeUser } from '../services/userService';
import customshineIcon from '../public/icon/customshine.png';

// Define animations (same as Homepage)
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.8;
  }
`;

// Create auth context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const theme = useTheme(); // Lấy theme

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Auto-initialize user profile when they login
      if (user) {
        try {
          const profile = await initializeUser();
          setUserProfile(profile);

          // Dispatch event to notify components that user was initialized
          window.dispatchEvent(new CustomEvent('userInitialized', {
            detail: { userProfile: profile, isNewUser: profile.createdAt === profile.updatedAt }
          }));
        } catch (error) {
          console.error('Error initializing user profile:', error);
          // Don't block the login process if initialization fails
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    // Cleanup subscription
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.light}05 100%)`, // đồng bộ màu nền splash
          backdropFilter: 'blur(99999px)',
          zIndex: 99999
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${fadeIn} 1s ease-in-out`
          }}>
            <img
              src={customshineIcon}
              alt="Customshine Logo"
              style={{
                width: 80,
                height: 80,
                marginBottom: 24,
                animation: `${pulse} 1.5s infinite ease-in-out`
              }}
            />
            <CircularProgress
              color="primary"
              size={40}
              thickness={4}
              sx={{
                opacity: 0.8
              }}
            />
          </Box>
        </Box>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
